import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useApiData } from "../lib/hooks";
import { formatTileSpec, formatTileNotes } from "../lib/formatTileSummary";
import { Avatar } from "./Avatar";
import { ErrorMessage } from "./ErrorMessage";
import { TargetFace } from "./Target";

// ── FormatTiles (#2319, M13.4) ──────────────────────────────────────────────
// "Your formats" — Home's pinned + recent format tiles, the fast path into
// scoring that replaces the old Score quick-action's bare "/new-round"
// jump. Self-contained (fetches its own data), the same pattern as
// ActiveRoundsList / ImportQueueStrip beside it on Home.
//
// Tapping a tile opens the START SHEET for that Format, prefilled from the
// archer's remembered picks (face, loadout, inner-ten, kind, tags) — one
// more tap on the sheet's Start creates the round. It deep-links to
// NewRound's `/new-round?format=<id>` (the same `format` query param the
// Format editor's "return=start" uses, #2321; Formats v2 is on by default so
// no `v2=1` is needed). The tile used to one-call `api.startFormat(id)` and
// jump straight to /score, replaying the remembered picks server-side with
// no chance to change them — owner decision 2026-09-16: Home must never
// bypass the setup screen, so `startFormat` is no longer called from here
// (StartSheet.jsx remains its caller).
//
// Tile set = the archer's favourite Formats, then Formats they've used
// recently (api.recentRoundTypeIds — the same distinct-round_type_id
// derivation the wizard TYPE step's Recent section already uses, #1394),
// deduped, ranked by remembered `last_used_at` DESC within each group, and
// capped at 4. Ranking needs every candidate's start-defaults row
// (last_used_at, plus the scorer/notes fields) up front — fetched in ONE
// call via api.listFormatStartDefaults (#2319) rather than a per-tile
// fan-out.
//
// Each tile is the same compact two-line ROW the /new-round picker's
// Pinned/Recent tiles use (#2384/#2385; shared `.format-tile*` CSS, SSOT in
// styles.css): face thumb LEFT, name + meta stacked RIGHT, and the WHOLE
// tile is the Start button (it opens the Start sheet) — no inner button, no
// dashed "New format" tile. The header's "More formats →" link opens the
// picker (Pinned / Recent / Library / Create) for everything beyond these
// four.
const TILE_CAP = 4;
// More candidates than the cap so favourites + recents still fill 4 tiles
// after dropping any recent id that duplicates a favourite.
const RECENT_CANDIDATES = 10;
// Thumbnail box = name line + body gap + meta line (see the `.format-tile`
// comment in styles.css); must match `.format-tile-thumb` and the picker's
// TILE_THUMB_SIZE.
const TILE_THUMB_SIZE = 36;
// The picker's route (App.jsx: the NewRound page hosting FormatPicker).
const PICKER_PATH = "/new-round";

/** The face to draw on a tile: the format's own face, else its first
 * allowed face (multi-face formats, #2326), else the app's generic 40 cm
 * default. */
function tileFace(rt) {
  return rt.face || rt.allowed_faces?.[0] || "wa40";
}

export function FormatTiles() {
  const navigate = useNavigate();
  const { archers } = useAuth();

  // A failed fetch is an ERROR state, never the new-user "Choose a format"
  // prompt (release review 2026-09-17). `reloadKey` sits in every dep list so
  // the alert's Retry refetches all five.
  const [reloadKey, setReloadKey] = useState(0);
  const rtQ   = useApiData(() => api.roundTypes(), [reloadKey]);
  const favQ  = useApiData(() => api.listFavouriteRoundTypes(), [reloadKey]);
  const recQ  = useApiData(() => api.recentRoundTypeIds(RECENT_CANDIDATES), [reloadKey]);
  const defQ  = useApiData(() => api.listFormatStartDefaults(), [reloadKey]);
  const tagQ  = useApiData(() => api.getTags(), [reloadKey]);
  const { data: roundTypes }    = rtQ;
  const { data: favourites }    = favQ;
  const { data: recentIds }     = recQ;
  const { data: startDefaults } = defQ;
  const { data: tags }          = tagQ;
  const loadError = rtQ.error || favQ.error || recQ.error || defQ.error || tagQ.error;

  const roundTypesById = useMemo(() => {
    const m = new Map();
    for (const rt of roundTypes ?? []) m.set(rt.id, rt);
    return m;
  }, [roundTypes]);

  const defaultsByRoundType = useMemo(() => {
    const m = new Map();
    for (const d of startDefaults ?? []) m.set(d.round_type_id, d);
    return m;
  }, [startDefaults]);

  const tagsById = useMemo(() => {
    const m = new Map();
    for (const t of tags ?? []) m.set(t.id, t.name);
    return m;
  }, [tags]);

  // useAuth's `archers` is [{ archer, role }] — the archers the signed-in
  // user can act for. Used only to resolve the badge's "<name> shoots" label
  // from a remembered default_archers[0].archer_id.
  const archerById = useMemo(() => {
    const m = new Map();
    for (const membership of archers ?? []) {
      if (membership?.archer?.id != null) m.set(membership.archer.id, membership.archer);
    }
    return m;
  }, [archers]);

  const tiles = useMemo(() => {
    function lastUsedDesc(a, b) {
      const da = defaultsByRoundType.get(a)?.last_used_at;
      const db = defaultsByRoundType.get(b)?.last_used_at;
      if (da && db) return new Date(db) - new Date(da);
      if (da) return -1;
      if (db) return 1;
      return 0;
    }

    const favIds = (favourites ?? []).map((f) => f.round_type_id);
    const favSet = new Set(favIds);
    const recentOnly = [...new Set(recentIds ?? [])].filter((id) => !favSet.has(id));

    const ids = [
      ...[...favIds].sort(lastUsedDesc),
      ...recentOnly.sort(lastUsedDesc),
    ].slice(0, TILE_CAP);

    return ids
      .map((id) => roundTypesById.get(id))
      .filter(Boolean)
      .map((rt) => ({ rt, defaults: defaultsByRoundType.get(rt.id) ?? null }));
  }, [favourites, recentIds, roundTypesById, defaultsByRoundType]);

  // Open the Start sheet for this Format, prefilled from the remembered
  // picks (NewRound reads `format` from the query string on mount and
  // renders StartSheet for it). Nothing is created here, so there is no
  // in-flight state and no error path on the tile itself.
  function handleStart(rt) {
    navigate(`${PICKER_PATH}?format=${rt.id}`);
  }

  return (
    <div className="section format-tiles-section">
      <div className="format-tiles-head">
        <span className="label">Your formats</span>
        <Link to={PICKER_PATH} className="panel-link">More formats →</Link>
      </div>
      {loadError && (
        <ErrorMessage
          message="Could not load your formats. Check your connection and try again."
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      )}
      {!loadError && tiles.length === 0 && (
        <div className="format-tiles-empty">
          Choose a format — browse the library or start one to see it here.
        </div>
      )}
      <div className="format-tiles">
        {!loadError && tiles.map(({ rt, defaults }) => {
          // Scorer slot (review-comment AC, #2319): a non-shooting host whose
          // remembered roster names exactly one other archer — "<name>
          // shoots · you score", an avatar with that label as its title.
          // Informational only: the Start sheet the tile opens is prefilled
          // from these same defaults, so it lands in scorer mode unless the
          // archer changes the roster there.
          const soloArcherId =
            defaults?.include_host === false && defaults?.default_archers?.length === 1
              ? defaults.default_archers[0]?.archer_id
              : null;
          const badgeArcher = soloArcherId != null ? archerById.get(soloArcherId) : null;
          const notes = formatTileNotes(defaults, tagsById);
          const spec = formatTileSpec(rt);
          const scorerLabel = badgeArcher ? `${badgeArcher.name} shoots · you score` : null;

          return (
            <button
              type="button"
              className="format-tile format-tile--home"
              key={rt.id}
              onClick={() => handleStart(rt)}
              aria-label={`Start ${rt.name}`}
            >
              <span className="format-tile-thumb" aria-hidden="true">
                <TargetFace face={tileFace(rt)} size={TILE_THUMB_SIZE} />
              </span>
              <span className="format-tile-body">
                <span className="format-tile-name">{rt.name}</span>
                <span className="format-tile-meta">
                  {`${spec}${notes ? ` · ${notes}` : ""}`}
                </span>
              </span>
              {badgeArcher && (
                <span className="format-tile-scorer" title={scorerLabel} aria-label={scorerLabel}>
                  <Avatar archer={badgeArcher} className="ws-avatar" />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
